import { useState } from 'react'
import { MapPin, Phone, Mail, Clock, Send, Facebook, Linkedin } from 'lucide-react'
import Hero from '../components/Hero'
import SEO from '../components/SEO'

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })

  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://rudovia.ac.zw'
      const response = await fetch(`${apiUrl}/api/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setSubmitted(true)
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        })
        setTimeout(() => setSubmitted(false), 6000)
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to send message. Please try again.')
      }
    } catch (error) {
      console.error('Error:', error)
      setError('Error sending message. Please check your connection.')
    }
  }

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['Rudovia Life College', 'Harare, Zimbabwe'],
      link: null
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+263 71 366 7029'],
      link: 'tel:+263713667029'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['admin@rudovia.ac.zw', 'academics@rudovia.ac.zw'],
      link: 'mailto:admin@rudovia.ac.zw'
    },
    {
      icon: Clock,
      title: 'Office Hours',
      details: ['Monday - Friday: 8:00 AM - 5:00 PM', 'Saturday: 9:00 AM - 1:00 PM'],
      link: null
    }
  ]

  const socialMedia = [
    { icon: Facebook, name: 'Facebook', link: 'https://www.facebook.com/Rudovia', color: 'hover:text-blue-600' },
    { icon: Linkedin, name: 'LinkedIn', link: 'https://www.linkedin.com/company/111399087/admin/dashboard/', color: 'hover:text-blue-700' }
  ]

  return (
    <>
      <SEO 
        title="Contact Us - Rudovia Life College"
        description="Get in touch with Rudovia Life College. Contact us for admissions, program information, or general inquiries. Located in Harare, Zimbabwe."
        keywords="contact Rudovia College, college contact Zimbabwe, admissions contact, Harare college contact"
      />
      <div className="pt-20">
        <Hero
          title="Contact Us"
          subtitle="We're here to answer your questions and help you begin your journey"
          image="/assets/images/pexels-ivan-s-8955325.jpg"
          height="h-[400px]"
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="heading-2 mb-6">Get in Touch</h2>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  Have questions about our programs, admissions, or campus life? We'd love to hear from you. Fill out the form and our team will respond within 24-48 hours.
                </p>

                <div className="space-y-6 mb-8">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-primary text-lg mb-1">{info.title}</h3>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-gray-600">
                            {info.link ? (
                              <a href={info.link} className="hover:text-secondary transition-colors">
                                {detail}
                              </a>
                            ) : (
                              detail
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <h3 className="font-bold text-primary text-xl mb-4">Connect With Us</h3>
                  <div className="flex space-x-4">
                    {socialMedia.map((social, index) => (
                      <a
                        key={index}
                        href={social.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-primary ${social.color} transition-colors`}
                        aria-label={social.name}
                      >
                        <social.icon className="w-6 h-6" />
                      </a>
                    ))}
                  </div>
                  <p className="mt-4 text-secondary font-semibold">
                    #RudoviaCollege #DoreenSpeaks
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-primary mb-6">Send Us a Message</h3>
                {submitted && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6 flex items-start space-x-4">
                    <Send className="w-8 h-8 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-900 font-bold text-lg mb-1">
                        Message Sent Successfully!
                      </p>
                      <p className="text-green-700">
                        Thank you for reaching out! We've received your message and will get back to you within 24-48 hours. Check your inbox for our response.
                      </p>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6 mb-6 flex items-start space-x-4">
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">!</span>
                    </div>
                    <div>
                      <p className="text-red-900 font-bold text-lg mb-1">
                        Message Failed to Send
                      </p>
                      <p className="text-red-700">
                        {error}
                      </p>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-primary font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-primary font-semibold mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-primary font-semibold mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-primary font-semibold mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                    >
                      <option value="">Select a subject</option>
                      <option value="admissions">Admissions Inquiry</option>
                      <option value="programs">Program Information</option>
                      <option value="financial-aid">Financial Aid</option>
                      <option value="campus-visit">Campus Visit</option>
                      <option value="general">General Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-primary font-semibold mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder="Tell us how we can help you..."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                    ></textarea>
                  </div>

                  <button type="submit" className="btn-primary w-full flex items-center justify-center space-x-2">
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Find Us on the Map</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Visit our campus in Harare, Zimbabwe
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl h-96">
 
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1788.7573986546574!2d31.046473304668734!3d-17.82166106036244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a51d22d3a787%3A0x4c868d9f9d0b75e6!2s52HW%2BJG6%2C%20Fife%20Ave%2C%20Harare!5e0!3m2!1sen!2szw!4v1769418240542!5m2!1sen!2szw"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Rudovia Life College Location"
              ></iframe>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gradient-to-r from-primary to-blue-900 text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Journey?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Don't wait—apply today and take the first step toward a transformative education at Rudovia Life College.
            </p>
            <a href="/admissions" className="btn-secondary">
              Apply Now
            </a>
          </div>
        </section>
      </div>
    </>
  )
}
